// The upload page's product-fields form, as plain functions: the server's
// fields (GET /photo-pipeline/product-fields/:sku) -> input values, input
// values -> the PUT body, and what is wrong with them. The server validates
// the same rules (photoPipeline/productFields.js); checking here lets the page
// mark a field before the clerk presses anything.
import { formatRingSize, parseRingSize, isQuarterSize } from './ringSize'

const LISTS = ['color', 'gemstones']
// Numbers above 0, and blank means "not filled in". Width and height are mm,
// length is inches, weight grams, stones carats, band width mm.
const NUMBERS = ['weight', 'width', 'height', 'length', 'band_width', 'center_stone', 'side_stones']
// Whole numbers of 0 or more; 0 clears the field on the server, like blank.
const COUNTS = ['side_stone_count']

const isBlank = text => String(text ?? '').trim() === ''

function inputValue(key, value) {
    if (LISTS.includes(key)) return Array.isArray(value) ? value : []
    if (key === 'ring_size') return formatRingSize(value)
    // A 0 g weight is what an untouched product holds, never a measurement.
    if (key === 'weight') return value > 0 ? String(value) : ''
    return value === null || value === undefined ? '' : String(value)
}

export function toDraft(fields = {}) {
    return Object.fromEntries(Object.entries(fields).map(([key, field]) => [key, inputValue(key, field?.value)]))
}

// One input value -> what the PUT sends. '' clears the field on the server.
function putValue(key, text) {
    if (LISTS.includes(key)) return text
    if (isBlank(text)) return ''
    if (key === 'ring_size') return parseRingSize(text)
    if (NUMBERS.includes(key) || COUNTS.includes(key)) return Number(text)
    return String(text).trim()
}

// The PUT's `fields`: only the keys whose value differs from what the server holds.
export function changedFields(draft, baseline) {
    const changed = {}
    for (const key of Object.keys(draft)) {
        const next = putValue(key, draft[key])
        if (JSON.stringify(next) !== JSON.stringify(putValue(key, baseline[key]))) changed[key] = next
    }
    return changed
}

// key -> 'required' | 'ringSize' | 'number' | 'wholeNumber', for each field that cannot be saved
// as is. A required field is always checked; any other only once the clerk has
// changed it from what the server holds (`baseline`), so untouched legacy
// values -- a pendant's center_stone of 0 -- never hold the upload back.
export function fieldProblems(draft, required = [], baseline = {}) {
    const problems = {}
    for (const [key, text] of Object.entries(draft)) {
        if (!required.includes(key) && JSON.stringify(text) === JSON.stringify(baseline[key])) continue
        const empty = LISTS.includes(key) ? text.length === 0 : isBlank(text)
        if (empty) {
            if (required.includes(key)) problems[key] = 'required'
            continue
        }
        if (key === 'ring_size') {
            const size = parseRingSize(text)
            if (size === null || !isQuarterSize(size) || size < 1 || size > 16) problems[key] = 'ringSize'
        } else if (NUMBERS.includes(key)) {
            const number = Number(text)
            if (!Number.isFinite(number) || number <= 0) problems[key] = 'number'
        } else if (COUNTS.includes(key)) {
            if (!/^\d+$/.test(String(text).trim())) problems[key] = 'wholeNumber'
        }
    }
    return problems
}

// A PUT is up to three Shopify writes and is not atomic; its 502 body says
// which fields landed. Null for any other failure, where nothing was written.
export function describeWriteFailure(response) {
    if (!Array.isArray(response?.landed) || !Array.isArray(response?.failed)) return null
    return { landed: response.landed, failed: response.failed, notAttempted: response.notAttempted ?? [] }
}
